import type { LandscapeId } from "./landscapes";

/**
 * Story step 6 — map published SE catalog categories onto narrative boards,
 * plus measurable chart series that exist in the DB.
 *
 * DB codes today: approvisionnement | regulation | culturel | socio-economique-transversal
 * (no digitized `soutien` SE rows / no $ US/ha/an). Spatial score maps 5.2–5.5
 * are not digitized — support is shown as a descriptive board only.
 */

export type StoryServiceItem = {
  readonly id: string;
  readonly libelle: string;
  readonly categorie: string;
};

export type StoryServiceCategoryId =
  "approvisionnement" | "socio-culturel-spirituel" | "support" | "regulation";

/** Boards that can host economic valuation bars (support has none published). */
export type StoryEcoBoardId = Exclude<StoryServiceCategoryId, "support">;

export type StoryServiceCategory = {
  readonly id: StoryServiceCategoryId;
  readonly label: string;
  readonly emptyHint: string;
};

export type StoryEcoIndicator = {
  readonly id: string;
  readonly shortLabel: string;
  readonly board: StoryEcoBoardId;
  /** Optional help text shown via a "?" tip on the chart (draft copy to review). */
  readonly tip?: string;
};

export type StorySupportDescription = {
  readonly id: string;
  readonly title: string;
  readonly description: string;
};

/** Meta / transversal catalog rows kept out of the public service chips. */
const EXCLUDED_SERVICE_IDS = new Set([
  "nbs-se-approvisionnement",
  "nbs-se-services-culturels",
  "nbs-se-valorisation-economique",
  "nbs-se-perceptions",
  "nbs-se-territoire",
]);

export const STORY_SERVICE_CATEGORIES: readonly StoryServiceCategory[] = [
  {
    id: "approvisionnement",
    label: "Services d’approvisionnement",
    emptyHint: "Aucun service d’approvisionnement prioritaire publié pour l’instant.",
  },
  {
    id: "socio-culturel-spirituel",
    label: "Service socio-culturel et spirituel",
    emptyHint: "Aucun service socio-culturel ou spirituel prioritaire publié pour l’instant.",
  },
  {
    id: "support",
    label: "Services de support",
    emptyHint: "Lecture descriptive — aucune série économique publiée pour le soutien.",
  },
  {
    id: "regulation",
    label: "Services de régulation",
    emptyHint: "Aucun service de régulation prioritaire publié pour l’instant.",
  },
] as const;

/**
 * Plain-language definition of what an ecosystem service is, and of the four
 * families the study groups them into — read before the measured boards below.
 */
export const STORY_SERVICES_ABOUT: readonly StorySupportDescription[] = [
  {
    id: "approvisionnement",
    title: "Approvisionnement : se nourrir, se soigner et disposer de matériaux.",
    description: "Exemples : aliments, bois de chauffe, plantes médicinales et eau.",
  },
  {
    id: "socio-culturel-spirituel",
    title: "Services socioculturels et spirituels : préserver les liens culturels avec la nature.",
    description: "Exemples : lieux sacrés, pratiques traditionnelles et transmission des savoirs.",
  },
  {
    id: "regulation",
    title: "Régulation : atténuer les risques et maintenir un environnement favorable.",
    description: "Exemples : limitation de l’érosion, filtration de l’eau et stockage du carbone.",
  },
  {
    id: "support",
    title: "Support : permettre aux écosystèmes de fonctionner et de se renouveler.",
    description:
      "Exemples : formation des sols, recyclage des nutriments et habitats nécessaires à la biodiversité.",
  },
] as const;

/**
 * Qualitative support board — the study counts 7 priority support SE
 * (executive summary, ch. 5) but no $ US/ha/an series are published.
 * Descriptions below follow MEA/BKM support functions cited by the model.
 */
export const STORY_SUPPORT_LEAD =
  "L’étude identifie 7 services de soutien prioritaires parmi les 58 SE retenus (résumé exécutif, chapitre 5). Aucune valorisation économique publiée ($ US/ha/an) n’est disponible pour cette famille : lecture descriptive uniquement.";

export const STORY_SUPPORT_DESCRIPTIONS: readonly StorySupportDescription[] = [
  {
    id: "formation-sols",
    title: "Formation et maintien des sols",
    description:
      "Processus qui construisent, stabilisent et renouvellent les sols — condition de fertilité et de productivité des écosystèmes forestiers et agricoles.",
  },
  {
    id: "cycle-nutriments",
    title: "Cycle des nutriments",
    description:
      "Circulation et recyclage des éléments nutritifs qui maintiennent la productivité écologique et soutiennent les autres services (approvisionnement, régulation).",
  },
  {
    id: "production-primaire",
    title: "Production primaire",
    description:
      "Production de biomasse par la végétation, base énergétique des chaînes alimentaires et fondement des bénéfices tirés de l’écosystème.",
  },
  {
    id: "habitat-refuge",
    title: "Habitat et refuge pour la biodiversité",
    description:
      "Offre d’espaces de vie et de refuge pour la faune et la flore, qui sous-tend la résilience des écosystèmes et la continuité des autres SE.",
  },
  {
    id: "conditions-ecologiques",
    title: "Maintien des conditions écologiques de base",
    description:
      "Ensemble des processus de soutien qui rendent possibles les services d’approvisionnement, de régulation et socioculturels — sans évaluation monétaire publiée dans le rapport.",
  },
] as const;

/** Economic valuation lines ($ US/ha/an, 2014) grouped for story charts. */
export const STORY_ECO_INDICATORS: readonly StoryEcoIndicator[] = [
  { id: "nbs-ind-eco-pfnl-total", shortLabel: "PFNL", board: "approvisionnement" },
  {
    id: "nbs-ind-eco-peche-continentale",
    shortLabel: "Pêche continentale",
    board: "approvisionnement",
  },
  {
    id: "nbs-ind-eco-bois-energie-construction",
    shortLabel: "Bois énergie / construction",
    board: "approvisionnement",
  },
  {
    id: "nbs-ind-eco-chasse-viande-brousse",
    shortLabel: "Chasse",
    board: "approvisionnement",
  },
  {
    id: "nbs-ind-eco-agriculture-fruits-foret",
    shortLabel: "Agriculture / fruits cultivés",
    board: "approvisionnement",
    tip: "Valeur des fruits cultivés cueillis en forêt liés aux activités agricoles (Tableau 4.11, p.94). Description à revoir avec Fayçal.",
  },
  {
    id: "nbs-ind-eco-elevage-microclimat",
    shortLabel: "Élevage domestique",
    board: "approvisionnement",
    tip: "Valeur de l’élevage d’animaux domestiques et du microclimat favorable associé (Tableau 4.11, p.94). Description à revoir avec Fayçal.",
  },
  {
    id: "nbs-ind-eco-ressources-genetiques",
    shortLabel: "Ressources génétiques",
    board: "approvisionnement",
  },
  {
    id: "nbs-ind-eco-eau-autoconsommation",
    shortLabel: "Approvisionnement en eau",
    board: "approvisionnement",
  },
  {
    id: "nbs-ind-eco-regulation-locale",
    shortLabel: "Régulation locale (R climat environnement)",
    board: "regulation",
  },
  {
    id: "nbs-ind-eco-regulation-climat-global",
    shortLabel: "Régulation climat global",
    board: "regulation",
  },
  {
    id: "nbs-ind-eco-services-culturels",
    shortLabel: "Services culturels",
    board: "socio-culturel-spirituel",
  },
  {
    id: "nbs-ind-eco-ecotourisme-esthetique",
    shortLabel: "Écotourisme & service recréatif",
    board: "socio-culturel-spirituel",
  },
] as const;

export const STORY_VET_INDICATOR_ID = "nbs-ind-eco-valeur-totale-vet";

/** Territory + metric that actually hold published land-cover series. */
export function landCoverQueryForLandscape(id: LandscapeId): {
  territoryId: string;
  metric: "part" | "superficie";
} {
  switch (id) {
    case "madina-oula":
      return { territoryId: "nbs-zon-madina-oula", metric: "part" };
    case "allassoya-moussaya":
      return { territoryId: "nbs-pay-allassoya-moussaya", metric: "part" };
    case "kounounkan":
      return { territoryId: "nbs-pay-kounounkan", metric: "superficie" };
  }
}

/**
 * Years that can be shown on land-cover charts for a study landscape.
 * Kounounkan Table 4.1 publishes variation rates only; the 2015 hectare series
 * for cultures is missing, so 2015 would read as a false 0 % pressure / incomplete pie.
 */
export function publishedLandCoverYears(
  years: readonly number[],
  landscapeId: LandscapeId,
): number[] {
  if (landscapeId === "kounounkan") {
    return years.filter((year) => year !== 2015);
  }
  return [...years];
}

/** @deprecated Use publishedLandCoverYears — same Kounounkan 2015 rule. */
export function pressureDonutYears(years: readonly number[], landscapeId: LandscapeId): number[] {
  return publishedLandCoverYears(years, landscapeId);
}

export function filterLandCoverRowsByPublishedYears<T extends { readonly year?: string | number }>(
  rows: readonly T[],
  landscapeId: LandscapeId,
): T[] {
  const allowed = new Set(
    publishedLandCoverYears(
      rows.map((row) => Number(row.year)).filter((year) => Number.isFinite(year)),
      landscapeId,
    ),
  );
  return rows.filter((row) => allowed.has(Number(row.year)));
}

export function isKounounkanTerritoryScope(
  territoryIds: readonly string[] | null | undefined,
): boolean {
  return territoryIds?.length === 1 && territoryIds[0] === "nbs-pay-kounounkan";
}

function boardForDbCategory(categorie: string): StoryServiceCategoryId | null {
  const key = categorie.trim().toLowerCase();
  if (key === "approvisionnement") return "approvisionnement";
  if (key === "regulation" || key === "régulation") return "regulation";
  if (key === "culturel" || key === "socio-culturel" || key === "spirituel") {
    return "socio-culturel-spirituel";
  }
  // Transversal socio-economic indicators are not SE boards.
  if (key === "socio-economique-transversal") return null;
  if (key === "pressions-menaces" || key === "enquete") return null;
  if (key === "objectifs-developpement-durable") return null;
  if (key === "soutien" || key === "support") return "support";
  return null;
}

export function groupStoryServices(
  services: readonly StoryServiceItem[],
): Record<StoryServiceCategoryId, StoryServiceItem[]> {
  const groups: Record<StoryServiceCategoryId, StoryServiceItem[]> = {
    approvisionnement: [],
    "socio-culturel-spirituel": [],
    support: [],
    regulation: [],
  };

  for (const service of services) {
    if (EXCLUDED_SERVICE_IDS.has(service.id)) continue;
    const board = boardForDbCategory(service.categorie);
    if (!board) continue;
    groups[board].push(service);
  }

  for (const key of Object.keys(groups) as StoryServiceCategoryId[]) {
    groups[key].sort((a, b) => a.libelle.localeCompare(b.libelle, "fr"));
  }

  return groups;
}

export type StorySeriesPoint = {
  readonly indicateurId?: string;
  readonly libelle?: string;
  readonly unit?: string | null;
  readonly points?: ReadonlyArray<{
    value: number;
    unit?: string | null;
    periodStart?: string | null;
    territoryLabel?: string | null;
  }>;
};

export type StoryEcoBar = {
  readonly label: string;
  readonly value: number;
  readonly tip?: string;
};

export function ecoBarsFromSeries(
  seriesById: Readonly<Record<string, StorySeriesPoint | null | undefined>>,
  board: StoryEcoBoardId,
): StoryEcoBar[] {
  const rows: StoryEcoBar[] = [];
  for (const meta of STORY_ECO_INDICATORS) {
    if (meta.board !== board) continue;
    const series = seriesById[meta.id];
    const value = series?.points?.[0]?.value;
    if (value == null || !Number.isFinite(value)) continue;
    rows.push({
      label: meta.shortLabel,
      value: Number(value),
      ...(meta.tip ? { tip: meta.tip } : {}),
    });
  }
  return rows.sort((a, b) => b.value - a.value);
}

/** Map priority SE catalog ids → published economic valuation series (2014). */
export const STORY_SE_TO_ECO_INDICATOR: Readonly<Record<string, string>> = {
  "nbs-se-pfnl": "nbs-ind-eco-pfnl-total",
  "nbs-se-bois-biomasse": "nbs-ind-eco-bois-energie-construction",
  "nbs-se-eau-douce": "nbs-ind-eco-eau-autoconsommation",
  "nbs-se-alimentation": "nbs-ind-eco-agriculture-fruits-foret",
  "nbs-se-services-culturels": "nbs-ind-eco-services-culturels",
  "nbs-se-ecotourisme": "nbs-ind-eco-ecotourisme-esthetique",
  "nbs-se-regulation-climat": "nbs-ind-eco-regulation-climat-global",
};

export type StoryServiceCard = {
  readonly id: string;
  readonly title: string;
  readonly categoryId: StoryServiceCategoryId;
  readonly categoryLabel: string;
  readonly description?: string;
  readonly ecoIndicateurId?: string;
};

/** Flat list of Étape 6 services (priority SE + support descriptions). */
export function storyServiceCards(priorities: readonly StoryServiceItem[]): StoryServiceCard[] {
  const grouped = groupStoryServices(priorities);
  const cards: StoryServiceCard[] = [];

  for (const category of STORY_SERVICE_CATEGORIES) {
    if (category.id === "support") {
      for (const entry of STORY_SUPPORT_DESCRIPTIONS) {
        cards.push({
          id: `support-${entry.id}`,
          title: entry.title,
          categoryId: "support",
          categoryLabel: category.label,
          description: entry.description,
        });
      }
      continue;
    }
    for (const service of grouped[category.id]) {
      cards.push({
        id: service.id,
        title: service.libelle,
        categoryId: category.id,
        categoryLabel: category.label,
        ...(STORY_SE_TO_ECO_INDICATOR[service.id]
          ? { ecoIndicateurId: STORY_SE_TO_ECO_INDICATOR[service.id] }
          : {}),
      });
    }
  }

  return cards;
}

/** Resolve aliased `StoryEcosystemServices` fields into indicateurId → series. */
export function storyEcoSeriesById(
  data: Record<string, unknown> | undefined,
): Record<string, StorySeriesPoint> {
  if (!data) return {};
  const aliasToId: Record<string, string> = {
    vet: "nbs-ind-eco-valeur-totale-vet",
    ecoUsage: "nbs-ind-eco-valeur-usage-totale",
    ecoOption: "nbs-ind-eco-valeur-option",
    ecoNonUsage: "nbs-ind-eco-valeur-non-usage",
    ecoPfnl: "nbs-ind-eco-pfnl-total",
    ecoPeche: "nbs-ind-eco-peche-continentale",
    ecoBois: "nbs-ind-eco-bois-energie-construction",
    ecoChasse: "nbs-ind-eco-chasse-viande-brousse",
    ecoAgriculture: "nbs-ind-eco-agriculture-fruits-foret",
    ecoMicroclimat: "nbs-ind-eco-elevage-microclimat",
    ecoGenetiques: "nbs-ind-eco-ressources-genetiques",
    ecoEau: "nbs-ind-eco-eau-autoconsommation",
    ecoRegLocale: "nbs-ind-eco-regulation-locale",
    ecoRegClimatGlobal: "nbs-ind-eco-regulation-climat-global",
    ecoServicesCulturels: "nbs-ind-eco-services-culturels",
    ecoEcotourisme: "nbs-ind-eco-ecotourisme-esthetique",
  };
  const out: Record<string, StorySeriesPoint> = {};
  for (const [alias, id] of Object.entries(aliasToId)) {
    const series = data[alias] as StorySeriesPoint | undefined;
    if (series) out[id] = series;
  }
  return out;
}
